import React, { useEffect, useState } from 'react';
import PrimaryButton from '../components/PrimaryButton';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import loginPageImage from '../assets/login-page.webp';
import OnboardingProgressHeader from '../components/OnboardingProgressHeader';
import { getAllUsers, saveOrUpdateUserOnLogin } from '../utils/dbAccess';
import { saveAffiliateToFirestore } from '../utils/affiliatesFirestore';
import firebase from 'firebase/compat/app';
import { firebaseConfig } from '../utils/firebaseConfig';
import { hasAllSelections, setPassedOnboarding } from '../utils/userSelections';
import { useFlutterGoogleAuth } from '../components/useFlutterGoogleAuth';
import { useFlutterAuth } from '../components/useFlutterFacebookAuth';
import { useFlutterAppleAuth } from '../components/useFlutterAppleAuth';
import { getDeepLinkParams } from '../components/useFlutterDeepLink';
import {
  trackPageView,
  trackClick,
  trackAuth,
  trackNavigation,
  trackLanguageSwitch,
  identifyUser,
} from '../utils/analytics';
import { getConnectionStatus, linkParentToChild } from '../utils/connectionCodes';
import SpinnerOverlay from '../components/SpinnerOverlay';

// App theme colors - matching the modern Kol Torah design system
const BACKGROUND_CREAM = '#FDF9F3';

const PENDING_AID_KEY = 'deeplink_aid';
const PENDING_CONNECTION_KEY = 'pending_connection_id';

type AppLang = 'en' | 'he' | 'fr' | 'es';

// Normalize any locale code (e.g. 'he-IL', 'iw', 'fr-FR', 'es-MX') to a supported language
function normalizeLang(lng: string | null | undefined): AppLang {
  const lower = (lng ?? '').toLowerCase();
  if (lower.startsWith('he') || lower.startsWith('iw')) return 'he';
  if (lower.startsWith('fr')) return 'fr';
  if (lower.startsWith('es')) return 'es';
  return 'en';
}

function getAidFromLocation(): string | null {
  const params = new URLSearchParams(window.location.search);
  const aid = params.get('aid');
  return aid && aid.trim() !== '' ? aid.trim() : null;
}

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const targetAfterLogin = returnTo ?? '/aliya-latora';

  // Persist aid from deeplink in localStorage (visible in DevTools, survives OAuth redirect)
  useEffect(() => {
    const fromUrl = getAidFromLocation();
    if (fromUrl) {
      localStorage.setItem(PENDING_AID_KEY, fromUrl);
      return;
    }
    // Fallback: in Flutter WebView the URL sometimes has no query (e.g. Android); get from bridge
    void getDeepLinkParams().then((params) => {
      const aid = params?.queryParameters?.aid?.trim();
      if (aid) localStorage.setItem(PENDING_AID_KEY, aid);
    });
  }, [location.search]);

  // Detect if running on iOS device (iPhone, iPad, iPod)
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Initialize language from localStorage/i18n, defaulting to 'he'
  const getInitialLanguage = (): AppLang => {
    // First check localStorage (where language is persisted)
    const savedLang = localStorage.getItem('i18nextLng');
    // Then check i18n.language
    const i18nLang = i18n.language;
    // Fallback to browser language, but default to Hebrew if not detected
    const browserLang = navigator.language;

    const detectedLang = savedLang || i18nLang || browserLang || 'he';
    if (!detectedLang || detectedLang === 'undefined' || detectedLang === 'null') {
      return 'he';
    }
    return normalizeLang(detectedLang);
  };

  const [, setLoginStatus] = useState(false);

  // Connection deep-link state
  const [connectionId] = useState<string | null>(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('connectionId');
  });
  const [codeStatus, setCodeStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  const [inviterName, setInviterName] = useState('');
  // true = code was created by a child (viewer is parent); false = created by a parent (viewer is child)
  const [codeCreatedByChild, setCodeCreatedByChild] = useState(true);

  // Validate connectionId deep-link on mount
  useEffect(() => {
    if (!connectionId) return;
    localStorage.setItem(PENDING_CONNECTION_KEY, connectionId);
    setCodeStatus('loading');
    getConnectionStatus(connectionId)
      .then((res) => {
        if (res.success && res.status === 'pending') {
          setInviterName(res.childName || '');
          setCodeCreatedByChild(!!res.childEmail && !res.parentEmail);
          setCodeStatus('valid');
        } else {
          setCodeStatus('invalid');
        }
      })
      .catch(() => setCodeStatus('invalid'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  // Initialize lang state - use a function to ensure we read from localStorage at initialization time
  // Default to Hebrew if no language is saved
  const [lang, setLang] = useState<AppLang>(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang) {
      return normalizeLang(savedLang);
    }
    // Default to Hebrew if no saved language
    return 'he';
  });
  const [user, setUser] = useState<any>();
  const [userName, setUserName] = useState<string>('');
  const { googleLogin, isFlutterWebView } = useFlutterGoogleAuth();
  useFlutterAuth();
  const { appleLogin } = useFlutterAppleAuth();
  // Blocks the whole UI with a spinner while waiting for Flutter to return the native login result
  const [isSigningIn, setIsSigningIn] = useState(false);

  const getUsers = async () => {
    const users = getAllUsers();
    console.log(users);
  };

  const saveAffiliateIfAid = async (userId: string) => {
    const aid = localStorage.getItem(PENDING_AID_KEY) ?? getAidFromLocation();
    if (!aid || !userId) return;
    try {
      await saveAffiliateToFirestore(userId, aid);
      localStorage.removeItem(PENDING_AID_KEY);
    } catch (e) {
      console.warn('Failed to save affiliate to Firestore:', e);
    }
  };

  // Sync language state with i18n on mount - prioritize localStorage (where AppOnboardingPage saves it)
  useEffect(() => {
    // Read directly from localStorage first (most reliable source)
    const savedLang = localStorage.getItem('i18nextLng');
    let detectedLang: AppLang;

    if (savedLang) {
      // Normalize the saved language
      detectedLang = normalizeLang(savedLang);
    } else {
      // Fallback to getInitialLanguage if nothing in localStorage
      detectedLang = getInitialLanguage();
    }

    // Ensure i18n is synced with the detected language
    if (detectedLang !== i18n.language) {
      i18n.changeLanguage(detectedLang);
    }
    // Update local state to match - this ensures the LanguageSwitcher shows the correct selection
    setLang(detectedLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to i18n language changes to keep state in sync (e.g., when changed from AppOnboardingPage or within this page)
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLang(normalizeLang(lng));
    };

    // Set up listener for language changes
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n]);

  useEffect(() => {
    firebase.initializeApp(firebaseConfig);
    const _user = firebase.auth().currentUser;
    if (_user) {
      setUser(_user);
    }
    // Only redirect to rabbi portal when the user has an approved rabbi session (rabbiToken).
    // Do NOT redirect when the user only requested registration (pending); they should stay on onboarding.
    const hasRabbiSession = !!localStorage.getItem('rabbiToken');
    if (hasRabbiSession) {
      trackNavigation('Onboarding', 'RabbiPortal', 'auto_redirect_rabbi');
      navigate('/rabbi', { replace: true });
      return;
    }
    // Track page view
    trackPageView('Onboarding');
    const isLoggedIn = !!localStorage.getItem('loggedInUser');

    // Logged-in user opened a connection deep link — link immediately and show success
    if (connectionId && isLoggedIn) {
      const raw = localStorage.getItem('loggedInUser');
      const loggedUser = raw
        ? (JSON.parse(raw) as { email?: string; displayName?: string; name?: string })
        : null;
      const email = loggedUser?.email ?? '';
      const name = loggedUser?.displayName ?? loggedUser?.name ?? '';
      if (email) {
        linkParentToChild({ code: connectionId, parentEmail: email, parentName: name })
          .then((result) => {
            localStorage.removeItem(PENDING_CONNECTION_KEY);
            if (result.success) {
              navigate('/user-portal', {
                replace: true,
                state: { connectionSuccess: true, connectedName: result.childName || '' },
              });
            } else {
              console.warn('linkParentToChild failed:', result.error);
              navigate('/user-portal', { replace: true });
            }
          })
          .catch(() => {
            localStorage.removeItem(PENDING_CONNECTION_KEY);
            navigate('/user-portal', { replace: true });
          });
        return;
      }
    }

    // Early redirect if logged in and all selections already exist — skip when user came to
    // sign in (e.g. from payments). Selections survive sign-out, so logged-out users must stay here.
    if (!returnTo && isLoggedIn && hasAllSelections()) {
      trackNavigation('Onboarding', 'UserPortal', 'auto_redirect');
      navigate('/user-portal', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnTo, connectionId]);

  // Listen to the Firebase Auth state and set the local state.
  useEffect(() => {
    const unregisterAuthObserver = firebase.auth().onAuthStateChanged((_user) => {
      setUser(_user);
    });
    return () => unregisterAuthObserver(); // Make sure we un-register Firebase observers when the component unmounts.
  }, []);

  useEffect(() => {
    //clearAllSelections();
    void getUsers();
  }, []);

  const handlePendingConnection = async (email: string, name: string): Promise<string | null> => {
    const pendingCode = localStorage.getItem(PENDING_CONNECTION_KEY);
    if (!pendingCode || !email) return null;
    try {
      const result = await linkParentToChild({
        code: pendingCode,
        parentEmail: email,
        parentName: name,
      });
      return result.childName || null;
    } catch (e) {
      console.warn('Failed to auto-link pending connection:', e);
      return null;
    } finally {
      localStorage.removeItem(PENDING_CONNECTION_KEY);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isSigningIn) return;
    trackClick('Sign In Google', 'Onboarding');
    // If running in browser (not Flutter WebView), skip login and go straight to AliyaLatoraPage
    if (!isFlutterWebView) {
      // TODO: DELETE THIS FAKE USER AFTER TESTING
      if (!localStorage.getItem('loggedInUser')) {
        localStorage.setItem(
          'loggedInUser',
          JSON.stringify({
            userId: 'guest-david-cohen',
            email: 'david.cohen@example.com',
            name: 'David Cohen',
            displayName: 'David Cohen',
            photoURL: null,
          }),
        );
      }
      // END FAKE USER
      trackAuth('sign_in', 'guest', true);
      trackNavigation('Onboarding', 'BirthDate', 'button');
      setPassedOnboarding();
      navigate(targetAfterLogin);
      return;
    }

    setIsSigningIn(true);
    try {
      const user = await googleLogin();
      if (user) {
        const uid = user.uid ?? null;
        const email = user.email ?? null;
        const name = user.displayName ?? null;
        setUserName(name ?? '');
        setLoginStatus(true);
        if (uid) {
          try {
            await saveOrUpdateUserOnLogin({
              uid,
              email,
              name,
              photoURL: user.photoURL ?? null,
            });
          } catch (e) {
            console.warn('Failed to save user to Firestore:', e);
          }
          await saveAffiliateIfAid(uid);
        }
        // Keep userId, email, name in localStorage (same userId as in users collection for subscriptions)
        localStorage.setItem(
          'loggedInUser',
          JSON.stringify({
            userId: uid ?? undefined,
            email: email ?? undefined,
            name: name ?? undefined,
            displayName: name ?? undefined,
            photoURL: user.photoURL ?? undefined,
            uid: uid ?? undefined,
          }),
        );
        trackAuth('sign_in', 'google', true);
        identifyUser(email ?? '', { name, email });
        const hadPendingConnection = !!localStorage.getItem(PENDING_CONNECTION_KEY);
        const connectedName = await handlePendingConnection(email ?? '', name ?? '');
        trackNavigation('Onboarding', 'BirthDate', 'button');
        setPassedOnboarding();
        if (hadPendingConnection) {
          navigate('/user-portal', {
            state: { connectionSuccess: true, connectedName: connectedName || '' },
          });
        } else {
          navigate(targetAfterLogin);
        }
      } else {
        trackAuth('sign_in', 'google', false);
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  // const handleFacebookSignIn = async () => {
  //   trackClick("Sign In Facebook", "Onboarding");
  //   const user = await loginWithFacebook();
  //   if (user) {
  //     setUserName(user.displayName);
  //     setLoginStatus(true);
  //     trackAuth("sign_in", "facebook", true);
  //     identifyUser(user.email || user.displayName, { name: user.displayName });
  //   } else {
  //     trackAuth("sign_in", "facebook", false);
  //   }
  // };

  const handleAppleSignIn = async () => {
    if (isSigningIn) return;
    trackClick('Sign In Apple', 'Onboarding');
    setIsSigningIn(true);
    try {
      const result = await appleLogin();
      if (result?.success && result.idToken) {
        const uid = result.uid ?? null;
        const email = result.email ?? null;
        const name = result.displayName ?? null;
        setUserName(name || '');
        setLoginStatus(true);
        if (uid) {
          try {
            await saveOrUpdateUserOnLogin({
              uid,
              email,
              name,
              photoURL: result.photoURL ?? null,
            });
          } catch (e) {
            console.warn('Failed to save user to Firestore:', e);
          }
          await saveAffiliateIfAid(uid);
        }
        // Keep userId, email, name in localStorage (same userId as in users collection)
        localStorage.setItem(
          'loggedInUser',
          JSON.stringify({
            userId: uid ?? undefined,
            email: email ?? undefined,
            name: name ?? undefined,
            displayName: name ?? undefined,
            photoURL: result.photoURL ?? undefined,
            uid: uid ?? undefined,
          }),
        );
        trackAuth('sign_in', 'apple', true);
        identifyUser(email || name || 'apple_user', { name, email });
        const hadPendingConnection = !!localStorage.getItem(PENDING_CONNECTION_KEY);
        const connectedName = await handlePendingConnection(email ?? '', name ?? '');
        trackNavigation('Onboarding', 'BirthDate', 'button');
        setPassedOnboarding();
        if (hadPendingConnection) {
          navigate('/user-portal', {
            state: { connectionSuccess: true, connectedName: connectedName || '' },
          });
        } else {
          navigate(targetAfterLogin);
        }
      } else {
        trackAuth('sign_in', 'apple', false);
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleRabbiLoginClick = () => {
    trackClick('Rabbi Login', 'Onboarding');
    const hasRabbiSession = !!localStorage.getItem('rabbiToken');
    if (hasRabbiSession) {
      trackNavigation('Onboarding', 'RabbiUpload', 'button');
      navigate('/rabbi');
      return;
    }
    trackNavigation('Onboarding', 'RabbiLogin', 'button');
    navigate('/rabbi-login');
  };

  return (
    <div
      className="onboarding-page"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: "var(--kol-font-sans, 'Heebo', sans-serif)",
        overflow: 'auto',
        position: 'relative',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {isSigningIn && <SpinnerOverlay message={t('logging_in') ?? undefined} />}
      {/* Content container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <OnboardingProgressHeader step={1} />

        {/* Hero section - login page image with bottom fade */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'visible',
            }}
          >
            <img
              src={loginPageImage}
              alt="Kol Torah"
              style={{
                width: '100%',
                display: 'block',
                objectFit: 'contain',
                verticalAlign: 'bottom',
                // Fade image to transparent at bottom for soft blend into background
                maskImage: `linear-gradient(to bottom, black 0%, black 55%, transparent 100%)`,
                WebkitMaskImage: `linear-gradient(to bottom, black 0%, black 55%, transparent 100%)`,
              }}
            />
            {/* Blurred border: gradient overlay blends image edge into cream background */}
            <div
              style={{
                position: 'absolute',
                bottom: -20,
                left: 0,
                right: 0,
                height: '100px',
                background: `linear-gradient(to bottom, transparent 0%, transparent 45%, ${BACKGROUND_CREAM} 75%, ${BACKGROUND_CREAM} 100%)`,
                pointerEvents: 'none',
              }}
            />
          </div>
        </div>

        {/* Invite banner — shown when app opened via connection deep-link */}
        {connectionId && (
          <div
            style={{
              width: '100%',
              maxWidth: 'min(320px, 90vw)',
              padding: '0 clamp(16px, 4vw, 24px)',
              marginBottom: 8,
              marginTop: 20,
              direction: lang === 'he' ? 'rtl' : 'ltr',
            }}
          >
            {codeStatus !== 'idle' && (
              <div style={{ textAlign: 'center', marginBottom: 10 }}>
                {codeStatus === 'loading' && (
                  <span style={{ fontSize: 13, color: '#6B7280' }}>...</span>
                )}
                {codeStatus === 'valid' && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: '#f0fdf4',
                      border: '1px solid #86efac',
                      borderRadius: 999,
                      padding: '6px 14px',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#22c55e" />
                      <path
                        d="M7 12l4 4 6-6"
                        stroke="white"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                      {t('invite_code_valid', 'קוד תקין')}
                      {inviterName ? ` - ${inviterName}` : ''}
                    </span>
                  </div>
                )}
                {codeStatus === 'invalid' && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: '#fef2f2',
                      border: '1px solid #fca5a5',
                      borderRadius: 999,
                      padding: '6px 14px',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#ef4444" />
                      <path
                        d="M8 8l8 8M16 8l-8 8"
                        stroke="white"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>
                      {t('invite_code_invalid', 'קוד לא תקין')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {codeStatus === 'valid' && (
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <p style={{ fontSize: 16, fontWeight: 800, color: '#1A1A2E', margin: '0 0 4px' }}>
                  🎉 {t('invite_mazel_tov', 'מזל טוב')} - {inviterName}{' '}
                  {t('invite_invited_you', 'הזמין אותך')}!
                </p>
                <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
                  {codeCreatedByChild
                    ? t('invite_connect_to_follow', 'התחבר כדי לעקוב אחרי ההתקדמות שלו')
                    : t('invite_connect_as_child', 'התחבר כדי שההורה יוכל לעקוב אחרי ההתקדמות שלך')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Buttons section */}
        <div
          style={{
            width: '100%',
            maxWidth: 'min(320px, 90vw)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(8px, 2vh, 12px)',
            padding: '0 clamp(16px, 4vw, 24px)',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          {user ? (
            <div className={'flex flex-col gap-3'}>
              <PrimaryButton
                onClick={async () => {
                  trackClick('Continue as User', 'Onboarding');
                  if (user.uid) await saveAffiliateIfAid(user.uid);
                  trackNavigation('Onboarding', 'BirthDate', 'button');
                  setPassedOnboarding();
                  navigate(targetAfterLogin);
                }}
                text={`Continue as ${user.displayName}`}
                variant="gold"
              />
              <PrimaryButton
                onClick={() => {
                  trackClick('Logout', 'Onboarding');
                  trackNavigation('Onboarding', 'Login', 'button');
                  navigate('/login');
                }}
                text={t('logout')}
                variant="white"
              />
            </div>
          ) : (
            <div className={'flex flex-col gap-3'}>
              {!userName && (
                <>
                  <button
                    onClick={handleGoogleSignIn}
                    disabled={isSigningIn}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: '16px',
                      border: 'none',
                      background: 'white',
                      boxShadow: '0 1px 2px rgba(60,64,67,.08),0 1px 3px 1px rgba(60,64,67,.08)',
                      cursor: isSigningIn ? 'not-allowed' : 'pointer',
                      fontSize: 'var(--kol-button-font-size, 16px)',
                      fontWeight: 400,
                      color: '#202124',
                      transition: 'background 0.2s',
                      opacity: isSigningIn ? 0.7 : 1,
                    }}
                  >
                    <span
                      style={{
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        direction: lang === 'he' ? 'rtl' : 'ltr',
                      }}
                    >
                      {userName ? userName : t('sign_in_google')}
                    </span>
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      style={{ width: 28, height: 28, marginLeft: 12 }}
                    />
                  </button>
                  {/* Show Apple Sign In only on iOS devices */}
                  {isIOS && (
                    <button
                      onClick={handleAppleSignIn}
                      disabled={isSigningIn}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        padding: '10px 16px',
                        borderRadius: '16px',
                        border: 'none',
                        background: 'white',
                        boxShadow: '0 1px 2px rgba(60,64,67,.08),0 1px 3px 1px rgba(60,64,67,.08)',
                        cursor: isSigningIn ? 'not-allowed' : 'pointer',
                        fontSize: 'var(--kol-button-font-size, 16px)',
                        fontWeight: 400,
                        color: '#202124',
                        transition: 'background 0.2s',
                        opacity: isSigningIn ? 0.7 : 1,
                      }}
                    >
                      <span
                        style={{
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          direction: lang === 'he' ? 'rtl' : 'ltr',
                        }}
                      >
                        {t('sign_in_apple')}
                      </span>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="#000"
                        style={{ marginLeft: 12 }}
                      >
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                    </button>
                  )}
                  {/* Continue as Guest - styled like rabbi login (pill, cream, border) */}

                  {codeStatus === 'idle' ? (
                    <button
                      onClick={() => {
                        trackClick('Continue as Guest', 'Onboarding');
                        trackAuth('sign_in', 'guest', true);
                        if (!localStorage.getItem('loggedInUser')) {
                          localStorage.setItem(
                            'loggedInUser',
                            JSON.stringify({
                              userId: `guest-${crypto.randomUUID()}`,
                              email: null,
                              name: t('guest', 'Guest'),
                              displayName: t('guest', 'Guest'),
                              photoURL: null,
                            }),
                          );
                        }
                        trackNavigation('Onboarding', 'BirthDate', 'button');
                        setPassedOnboarding();
                        navigate(targetAfterLogin);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 24px',
                        borderRadius: '9999px',
                        border: '1px solid #3F4756',
                        background: BACKGROUND_CREAM,
                        color: '#3F4756',
                        fontSize: 'var(--kol-button-font-size, 16px)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        textAlign: 'center',
                        direction: lang === 'he' ? 'rtl' : 'ltr',
                      }}
                    >
                      {t('onboarding_guest')}
                    </button>
                  ) : (
                    ''
                  )}
                </>
              )}
              {userName && (
                <PrimaryButton
                  onClick={() => {
                    trackClick('Continue as Logged User', 'Onboarding');
                    trackNavigation('Onboarding', 'BirthDate', 'button');
                    setPassedOnboarding();
                    navigate(targetAfterLogin);
                  }}
                  text={`Continue as ${userName}`}
                  variant="gold"
                />
              )}
            </div>
          )}
        </div>

        {/* Spacer to push Rabbi buttons to bottom */}
        <div style={{ flexGrow: 1 }} />

        {/* Rabbi login & registration - stacked at bottom: login first, register below */}
        <div
          style={{
            marginBottom: 'clamp(20px, 5vh, 32px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <button
            type="button"
            onClick={handleRabbiLoginClick}
            style={{
              display: 'none',
              padding: '6px 10px',
              border: 'none',
              background: 'transparent',
              color: 'rgba(63, 71, 86, 0.85)',
              fontSize: '13px',
              fontWeight: 400,
              cursor: 'pointer',
              textAlign: 'center',
              direction: lang === 'he' ? 'rtl' : 'ltr',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {t('rabbi_login')}
          </button>
          {/*<Link*/}
          {/*  to="/register-rabbi-request"*/}
          {/*  title={t("rabbi_register_link") ?? undefined}*/}
          {/*  style={{*/}
          {/*    padding: "6px 10px",*/}
          {/*    border: "none",*/}
          {/*    background: "transparent",*/}
          {/*    color: "rgba(63, 71, 86, 0.85)",*/}
          {/*    fontSize: "13px",*/}
          {/*    fontWeight: 400,*/}
          {/*    cursor: "pointer",*/}
          {/*    textAlign: "center",*/}
          {/*    direction: lang === "he" ? "rtl" : "ltr",*/}
          {/*    textDecoration: "underline",*/}
          {/*    textUnderlineOffset: "2px",*/}
          {/*  }}*/}
          {/*>*/}
          {/*  {t("rabbi_register_link")}*/}
          {/*</Link>*/}
          <Link
            to="/terms-of-use"
            style={{
              padding: '6px 10px',
              border: 'none',
              background: 'transparent',
              color: 'rgba(63, 71, 86, 0.85)',
              fontSize: '13px',
              fontWeight: 400,
              cursor: 'pointer',
              textAlign: 'center',
              direction: lang === 'he' ? 'rtl' : 'ltr',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {t('terms_of_use')}
          </Link>
          <div style={{ marginTop: '6px' }}>
            <LanguageSwitcher
              current={lang}
              onSwitch={(l) => {
                trackLanguageSwitch(lang, l);
                setLang(normalizeLang(l));
                i18n.changeLanguage(l);
                // Persist the bare code so refresh keeps the choice (avoids en-US → Hebrew overwrite elsewhere)
                localStorage.setItem('i18nextLng', l);
              }}
            />
          </div>
        </div>
      </div>
      {/* <BottomNav /> */}
    </div>
  );
};

export default OnboardingPage;
